import React, { useEffect, useMemo, useState } from "react";
import { Search, Star, ThumbsUp, Meh, ThumbsDown, MessageSquare, RefreshCw } from "lucide-react";
import useCrmApi from "../../../../components/hook/crm-req";

// ── Types — matches CustomerFeedback / CustomerFeedbackHistory from
// docs/customer-feedback-ui-integration.md exactly; nothing here is
// hardcoded or invented beyond what that contract documents. ───────────────

type CustomerRating = 1 | 2 | 3 | 4 | 5;

interface CustomerFeedback {
  id: string;
  ticket_id: string;
  ticket_reference: string;
  issue_type: string | null;
  customer_rating: CustomerRating;
  customer_feedback: string | null;
  feedback_submitted_at: string;
  updated_at: string;
}

interface CustomerFeedbackHistory {
  items: CustomerFeedback[];
  total: number;
  average_rating: number | null;
}

type SentimentFilter = "all" | "positive" | "neutral" | "negative";

const sentimentOf = (rating: number): Exclude<SentimentFilter, "all"> =>
  rating >= 4 ? "positive" : rating === 3 ? "neutral" : "negative";

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const datePart = isToday
    ? "Today"
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const timePart = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${datePart} · ${timePart}`;
};

// A feedback record counts as "Edited" only once its update meaningfully
// postdates the original submission (more than a minute), not from
// float/serialization noise between two near-identical timestamps.
const isEdited = (f: CustomerFeedback) => {
  const submitted = new Date(f.feedback_submitted_at).getTime();
  const updated = new Date(f.updated_at).getTime();
  return Number.isFinite(submitted) && Number.isFinite(updated) && updated - submitted > 60_000;
};

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: string;
  active?: boolean;
  onClick?: () => void;
}> = ({ icon, label, value, sub, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={`text-left bg-white dark:bg-gray-900 rounded-xl border p-4 flex-1 min-w-[150px] transition-colors ${
      active ? "border-indigo-400 dark:border-indigo-500 ring-1 ring-indigo-200 dark:ring-indigo-500/30" : "border-gray-100 dark:border-gray-800"
    } ${onClick ? "cursor-pointer hover:border-indigo-200 dark:hover:border-indigo-800" : "cursor-default"}`}
  >
    <div className="flex items-center gap-2 text-[#7B7B7B] font-semibold dark:text-gray-500 text-xs mb-2">
      {icon}
      <span>{label}</span>
    </div>
    <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
    {sub && <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5">{sub}</p>}
  </button>
);

const StarRow: React.FC<{ rating: number }> = ({ rating }) => (
  <span className="inline-flex items-center gap-1" role="img" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={12} className={n <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-200 dark:text-gray-700"} />
    ))}
    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 ml-1">{rating}/5</span>
  </span>
);

// ── FeedbackTab ─────────────────────────────────────────────────────────────
// GET /contacts/{contact_id}/feedback — lifetime history for this customer.
// The endpoint is paginated (limit/offset), so this fetches every page up
// front (capped defensively) to get an accurate sentiment breakdown and a
// working client-side search — the API doesn't expose a distribution or
// search endpoint of its own (see "Current API limitation" in the guide).
const PAGE_SIZE = 100;
const MAX_PAGES = 25;

const FeedbackTab: React.FC<{ contactId?: string }> = ({ contactId }) => {
  const token = localStorage.getItem("authToken");
  const [feedbacks, setFeedbacks] = useState<CustomerFeedback[]>([]);
  const [total, setTotal] = useState(0);
  const [averageRating, setAverageRating] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sentimentFilter, setSentimentFilter] = useState<SentimentFilter>("all");

  const { makeRequest: getFeedback } = useCrmApi(`/contacts/${contactId || ""}/feedback`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const fetchAll = async () => {
    if (!contactId) return;
    setLoading(true);
    setError(null);
    try {
      let offset = 0;
      let all: CustomerFeedback[] = [];
      let totalCount = 0;
      let avg: number | null = null;

      for (let page = 0; page < MAX_PAGES; page++) {
        const [res, status] = await getFeedback(undefined, { limit: PAGE_SIZE, offset });
        if (status < 200 || status >= 300) {
          throw new Error(res?.detail || res?.message || "Failed to load feedback.");
        }
        const data: CustomerFeedbackHistory = res;
        const items = Array.isArray(data?.items) ? data.items : [];
        totalCount = typeof data?.total === "number" ? data.total : all.length + items.length;
        avg = typeof data?.average_rating === "number" ? data.average_rating : null;
        all = all.concat(items);
        offset += PAGE_SIZE;
        if (items.length < PAGE_SIZE || all.length >= totalCount) break;
      }

      setFeedbacks(all);
      setTotal(totalCount);
      setAverageRating(avg);
    } catch (err: any) {
      setError(err?.message || "Failed to load feedback.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const counts = useMemo(() => {
    const c = { positive: 0, neutral: 0, negative: 0 };
    feedbacks.forEach((f) => {
      c[sentimentOf(f.customer_rating)] += 1;
    });
    return c;
  }, [feedbacks]);

  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

  const filtered = useMemo(() => {
    let list = feedbacks;
    if (sentimentFilter !== "all") {
      list = list.filter((f) => sentimentOf(f.customer_rating) === sentimentFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (f) =>
          (f.customer_feedback || "").toLowerCase().includes(q) ||
          (f.issue_type || "").toLowerCase().includes(q) ||
          f.ticket_reference.toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => new Date(b.feedback_submitted_at).getTime() - new Date(a.feedback_submitted_at).getTime());
  }, [feedbacks, sentimentFilter, search]);

  if (loading) {
    return (
      <div className="p-4 space-y-4 animate-pulse">
        <div className="flex gap-3 flex-wrap">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex-1 min-w-[150px] h-[86px] rounded-xl bg-gray-100 dark:bg-gray-800" />
          ))}
        </div>
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 rounded-xl bg-gray-100 dark:bg-gray-800" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{error}</p>
        <button
          type="button"
          onClick={fetchAll}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700"
        >
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Summary row — average_rating/total come straight from the API,
          never recomputed from a partial page. */}
      <div className="flex gap-3 flex-wrap">
        <SummaryCard
          icon={<Star size={13} className="fill-yellow-400 text-yellow-400" />}
          label="Overall Rating"
          value={averageRating != null ? `${averageRating.toFixed(1)}/5` : "—"}
          sub={`${total.toLocaleString()} response${total === 1 ? "" : "s"}`}
        />
        <SummaryCard
          icon={<ThumbsUp size={13} className="text-green-500" />}
          label="Positive"
          value={`${counts.positive} (${pct(counts.positive)}%)`}
          sub="Rating 4-5"
          active={sentimentFilter === "positive"}
          onClick={() => setSentimentFilter((f) => (f === "positive" ? "all" : "positive"))}
        />
        <SummaryCard
          icon={<Meh size={13} className="text-amber-500" />}
          label="Neutral"
          value={`${counts.neutral} (${pct(counts.neutral)}%)`}
          sub="Rating 3"
          active={sentimentFilter === "neutral"}
          onClick={() => setSentimentFilter((f) => (f === "neutral" ? "all" : "neutral"))}
        />
        <SummaryCard
          icon={<ThumbsDown size={13} className="text-red-500" />}
          label="Negative"
          value={`${counts.negative} (${pct(counts.negative)}%)`}
          sub="Rating 1-2"
          active={sentimentFilter === "negative"}
          onClick={() => setSentimentFilter((f) => (f === "negative" ? "all" : "negative"))}
        />
        <SummaryCard
          icon={<MessageSquare size={13} className="text-indigo-400" />}
          label="Responses"
          value={total.toLocaleString()}
          sub="Total recorded feedback"
        />
      </div>

      {/* Toolbar — search runs client-side over the full loaded history;
          the API doesn't expose a search/filter param of its own. */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[160px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search feedback or ticket reference"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500/30"
          />
        </div>
        {sentimentFilter !== "all" && (
          <button
            type="button"
            onClick={() => setSentimentFilter("all")}
            className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Clear filter
          </button>
        )}
      </div>

      {/* History list */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm">
          {feedbacks.length === 0
            ? "No feedback has been received from this customer yet."
            : "No feedback matches your search."}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden divide-y divide-gray-50 dark:divide-gray-800">
          {filtered.map((f) => (
            <div key={f.id} className="flex items-start justify-between gap-3 px-4 py-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <StarRow rating={f.customer_rating} />
                  {f.issue_type && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                      {f.issue_type}
                    </span>
                  )}
                  <span className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Ticket {f.ticket_reference}</span>
                  {isEdited(f) && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                      Edited
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 break-words">
                  {f.customer_feedback || <span className="text-[#7B7B7B] font-semibold dark:text-gray-500 italic">No written feedback.</span>}
                </p>
              </div>
              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap shrink-0">
                Submitted {formatDate(f.feedback_submitted_at)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedbackTab;
